import { useTranslation } from 'react-i18next';

import { noRecommendationKey } from '@entities/conclusion';
import { ROUTES } from '@shared/config/routes';
import { ButtonLink } from '@shared/ui';

import styles from './NoRecommendation.module.css';

interface NoRecommendationProps {
  /** Код причины из `no_recommendation_reason`. */
  reason: string;
  searchId: string;
  /** Замены ещё возможны: состав четвёрки не заморожен (§34.5). */
  canReplace: boolean;
}

/**
 * «Ничего не подходит» — полноценный путь, а не тупик (FE-250, MVP_PRD §38.4).
 *
 * Три правила экрана:
 *
 * * **причина названа кодом с переводом**, а не общим «ничего не найдено».
 *   «Ни один автохаус не ответил» и «у всех критично не хватает сведений» —
 *   разные ситуации, и делать с ними нужно разное;
 * * **выходов три, и все рабочие**: правка критериев (бесплатно, §35.4),
 *   расширение географии (§43.3) и — **до заморозки** — замена вариантов.
 *   После заморозки замен нет, и предлагать их значило бы соврать (§34.5);
 * * **это не приговор автомобилям и не оценка продавцов.** Формулировка
 *   говорит о собранных сведениях, а не о качестве вариантов.
 */
export function NoRecommendation({ reason, searchId, canReplace }: NoRecommendationProps) {
  const { t } = useTranslation();

  return (
    <section className={styles.wrap} aria-label={t('conclusion.noRecommendation.title')}>
      <h2 className={styles.title}>{t('conclusion.noRecommendation.title')}</h2>
      <p className={styles.reason}>{t(noRecommendationKey(reason))}</p>
      <p className={styles.note}>{t('conclusion.noRecommendation.note')}</p>

      <ul className={styles.options}>
        <li>
          <ButtonLink to={ROUTES.searchAssistant(searchId)} variant="secondary">
            {t('conclusion.noRecommendation.editCriteria')}
          </ButtonLink>
          <span className={styles.hint}>{t('conclusion.noRecommendation.editCriteriaHint')}</span>
        </li>
        {canReplace ? (
          <li>
            <ButtonLink to={ROUTES.searchTop4(searchId)} variant="secondary">
              {t('conclusion.noRecommendation.replace')}
            </ButtonLink>
            <span className={styles.hint}>{t('conclusion.noRecommendation.replaceHint')}</span>
          </li>
        ) : (
          <li>
            {/* Замен больше не будет, и экран говорит **почему**, а не
                показывает неактивную кнопку без объяснения (§34.5). */}
            <span className={styles.hint}>{t('conclusion.noRecommendation.frozen')}</span>
          </li>
        )}
      </ul>
    </section>
  );
}
